import React, { useState, useEffect, useCallback } from 'react'
import { api } from '../services/authService'
import LeadDetailModal from '../components/marketing/LeadDetailModal'
import {
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  UserGroupIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

const SECTORS = [
  'Construção Civil', 'Mineração', 'Pintura Viária', 'Saneamento',
  'Energia', 'Agronegócio', 'Logística', 'Indústria', 'Outros'
]

const COMPANY_SIZES = [
  { value: 'micro', label: 'Micro (até 9 func.)' },
  { value: 'pequena', label: 'Pequena (10-49)' },
  { value: 'media', label: 'Média (50-249)' },
  { value: 'grande', label: 'Grande (250+)' }
]

const MarketingLeadsPage = () => {
  const [leads, setLeads] = useState([])
  const [loading, setLoading] = useState(true)
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 25, pages: 1 })
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState({ sector: '', company_size: '', status: '' })
  const [showFilters, setShowFilters] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [editingLead, setEditingLead] = useState(null)
  const [selectedLead, setSelectedLead] = useState(null)
  const [stages, setStages] = useState([])
  const [sectors, setSectors] = useState([])

  useEffect(() => {
    loadStages()
    loadSectors()
  }, [])

  useEffect(() => {
    loadLeads()
  }, [pagination.page, filters])

  const loadStages = async () => {
    try {
      const res = await api.get('/api/marketing/pipeline/stages')
      setStages(res.data)
    } catch (error) {
      console.error('Erro ao carregar stages:', error)
    }
  }

  const loadSectors = async () => {
    try {
      const res = await api.get('/api/marketing/leads/sectors')
      setSectors(res.data)
    } catch (error) {
      console.error('Erro ao carregar setores:', error)
    }
  }

  const loadLeads = async () => {
    setLoading(true)
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        sort_by: 'created_at',
        sort_order: 'desc'
      }
      if (search) params.search = search
      if (filters.sector) params.sector = filters.sector
      if (filters.company_size) params.company_size = filters.company_size
      if (filters.status) params.status = filters.status

      const res = await api.get('/api/marketing/leads', { params })
      setLeads(res.data.data || [])
      setPagination(prev => ({ ...prev, ...res.data.pagination }))
    } catch (error) {
      console.error('Erro ao carregar leads:', error)
      toast.error('Erro ao carregar leads')
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (e) => {
    e.preventDefault()
    setPagination(prev => ({ ...prev, page: 1 }))
    loadLeads()
  }

  const handleDelete = async (id) => {
    if (!confirm('Tem certeza que deseja excluir este lead?')) return
    try {
      await api.delete(`/api/marketing/leads/${id}`)
      toast.success('Lead excluído com sucesso')
      loadLeads()
    } catch (error) {
      toast.error('Erro ao excluir lead')
    }
  }

  const handleSaveLead = async (data) => {
    try {
      if (editingLead) {
        await api.put(`/api/marketing/leads/${editingLead.id}`, data)
        toast.success('Lead atualizado com sucesso')
      } else {
        await api.post('/api/marketing/leads', data)
        toast.success('Lead criado com sucesso')
      }
      setShowForm(false)
      setEditingLead(null)
      loadLeads()
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar lead')
    }
  }

  return (
    <div className="p-4 lg:p-6 space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-800 dark:text-white">Leads</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">{pagination.total} empresas cadastradas</p>
        </div>
        <button
          onClick={() => { setEditingLead(null); setShowForm(true) }}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
        >
          <PlusIcon className="w-4 h-4" />
          Novo Lead
        </button>
      </div>

      {/* Busca e Filtros */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-2.5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por nome, razão social ou CNPJ..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <button type="submit" className="px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition">
              Buscar
            </button>
          </form>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-2 rounded-lg transition flex items-center gap-2 ${showFilters ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200'}`}
          >
            <FunnelIcon className="w-4 h-4" />
            Filtros
          </button>
        </div>

        {/* Filtros expandidos */}
        {showFilters && (
          <div className="mt-3 pt-3 border-t dark:border-gray-700 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <select
              value={filters.sector}
              onChange={e => setFilters(prev => ({ ...prev, sector: e.target.value }))}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
            >
              <option value="">Todos os setores</option>
              {(sectors.length > 0 ? sectors : SECTORS).map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <select
              value={filters.company_size}
              onChange={e => setFilters(prev => ({ ...prev, company_size: e.target.value }))}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
            >
              <option value="">Todos os portes</option>
              {COMPANY_SIZES.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            <select
              value={filters.status}
              onChange={e => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
            >
              <option value="">Todos os status</option>
              <option value="active">Ativo</option>
              <option value="inactive">Inativo</option>
            </select>
          </div>
        )}
      </div>

      {/* Tabela de Leads */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : leads.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-500">
            <BuildingOfficeIcon className="w-16 h-16 mb-4 text-gray-300" />
            <p className="text-lg font-medium">Nenhum lead encontrado</p>
            <p className="text-sm mt-1">Cadastre seu primeiro lead para começar</p>
            <button
              onClick={() => { setEditingLead(null); setShowForm(true) }}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              Cadastrar Lead
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-750">
                  <tr className="text-left text-gray-500 dark:text-gray-400">
                    <th className="px-4 py-3 font-medium">Empresa</th>
                    <th className="px-4 py-3 font-medium hidden md:table-cell">Setor</th>
                    <th className="px-4 py-3 font-medium hidden lg:table-cell">CNPJ</th>
                    <th className="px-4 py-3 font-medium text-center">Score</th>
                    <th className="px-4 py-3 font-medium">Etapa</th>
                    <th className="px-4 py-3 font-medium hidden lg:table-cell">Contatos</th>
                    <th className="px-4 py-3 font-medium text-right">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {leads.map(lead => (
                    <tr key={lead.id} className="hover:bg-gray-50 dark:hover:bg-gray-750 cursor-pointer" onClick={() => setSelectedLead(lead)}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-800 dark:text-white">{lead.company_name}</div>
                        {lead.trade_name && <div className="text-xs text-gray-500">{lead.trade_name}</div>}
                        {lead.city && <div className="text-xs text-gray-400">{lead.city}/{lead.state}</div>}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400 hidden md:table-cell">{lead.sector || '-'}</td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400 hidden lg:table-cell font-mono text-xs">{lead.cnpj || '-'}</td>
                      <td className="px-4 py-3 text-center">
                        <ScoreBadge score={Number(lead.score) || 0} />
                      </td>
                      <td className="px-4 py-3">
                        {lead.stage_name ? (
                          <span className="px-2 py-1 rounded-full text-xs font-medium text-white whitespace-nowrap" style={{ backgroundColor: lead.stage_color }}>
                            {lead.stage_name}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span className="flex items-center gap-1 text-gray-500">
                          <UserGroupIcon className="w-4 h-4" />
                          {lead.contacts_count || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => { setEditingLead(lead); setShowForm(true) }}
                            className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                            title="Editar"
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(lead.id)}
                            className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                            title="Excluir"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Paginação */}
            {pagination.pages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t dark:border-gray-700">
                <span className="text-sm text-gray-500">
                  Mostrando {((pagination.page - 1) * pagination.limit) + 1} a {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total}
                </span>
                <div className="flex gap-1">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page <= 1}
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeftIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page >= pagination.pages}
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRightIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal de Formulário */}
      {showForm && (
        <LeadFormModal
          lead={editingLead}
          stages={stages}
          onSave={handleSaveLead}
          onClose={() => { setShowForm(false); setEditingLead(null) }}
        />
      )}

      {/* Modal de Detalhe */}
      {selectedLead && (
        <LeadDetailModal
          leadId={selectedLead.id}
          onClose={() => setSelectedLead(null)}
          onEdit={(lead) => { setSelectedLead(null); setEditingLead(lead); setShowForm(true) }}
          onRefresh={loadLeads}
        />
      )}
    </div>
  )
}

// Componente Score Badge
const ScoreBadge = ({ score }) => {
  let color = 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
  if (score >= 100) color = 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
  else if (score >= 50) color = 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300'
  else if (score > 0) color = 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'

  return <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${color}`}>{score}</span>
}

// Componente Formulário de Lead (Modal)
const LeadFormModal = ({ lead, stages, onSave, onClose }) => {
  const [form, setForm] = useState({
    company_name: lead?.company_name || '',
    trade_name: lead?.trade_name || '',
    cnpj: lead?.cnpj || '',
    sector: lead?.sector || '',
    company_size: lead?.company_size || '',
    employee_count: lead?.employee_count || '',
    address: lead?.address || '',
    city: lead?.city || '',
    state: lead?.state || '',
    zip_code: lead?.zip_code || '',
    website: lead?.website || '',
    lead_source: lead?.lead_source || '',
    notes: lead?.notes || '',
    contacts: []
  })
  const [saving, setSaving] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.company_name.trim()) {
      toast.error('Nome da empresa é obrigatório')
      return
    }
    setSaving(true)
    await onSave(form)
    setSaving(false)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-800 dark:text-white">
            {lead ? 'Editar Lead' : 'Novo Lead'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Razão Social *</label>
              <input
                type="text"
                value={form.company_name}
                onChange={e => setForm(prev => ({ ...prev, company_name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nome Fantasia</label>
              <input
                type="text"
                value={form.trade_name}
                onChange={e => setForm(prev => ({ ...prev, trade_name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">CNPJ</label>
              <input
                type="text"
                value={form.cnpj}
                onChange={e => setForm(prev => ({ ...prev, cnpj: e.target.value }))}
                placeholder="00.000.000/0001-00"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Setor</label>
              <select
                value={form.sector}
                onChange={e => setForm(prev => ({ ...prev, sector: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
              >
                <option value="">Selecione...</option>
                {SECTORS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Porte</label>
              <select
                value={form.company_size}
                onChange={e => setForm(prev => ({ ...prev, company_size: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
              >
                <option value="">Selecione...</option>
                {COMPANY_SIZES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Cidade</label>
              <input
                type="text"
                value={form.city}
                onChange={e => setForm(prev => ({ ...prev, city: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">UF</label>
              <input
                type="text"
                value={form.state}
                onChange={e => setForm(prev => ({ ...prev, state: e.target.value }))}
                maxLength={2}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Website</label>
              <input
                type="text"
                value={form.website}
                onChange={e => setForm(prev => ({ ...prev, website: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Origem do Lead</label>
              <select
                value={form.lead_source}
                onChange={e => setForm(prev => ({ ...prev, lead_source: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
              >
                <option value="">Selecione...</option>
                <option value="google_ads">Google Ads</option>
                <option value="meta_ads">Meta/Instagram Ads</option>
                <option value="organic">Busca Orgânica</option>
                <option value="referral">Indicação</option>
                <option value="feira">Feira/Evento</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="cold_call">Prospecção Ativa</option>
                <option value="website">Site</option>
                <option value="linkedin">LinkedIn</option>
                <option value="other">Outro</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Observações</label>
              <textarea
                value={form.notes}
                onChange={e => setForm(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t dark:border-gray-700">
            <button type="button" onClick={onClose} className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition">
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
            >
              {saving ? 'Salvando...' : (lead ? 'Atualizar' : 'Cadastrar')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default MarketingLeadsPage
